import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

// Variants and sizes for consistent button styling
const variants = {
  primary: 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:shadow-lg hover:shadow-cyan-500/20',
  secondary: 'bg-slate-800 text-white hover:bg-slate-700',
  outline: 'bg-transparent border hover:bg-white/10',
  ghost: 'bg-transparent hover:bg-slate-100'
};

const sizes = {
  sm: 'py-2 px-4 text-sm',
  md: 'py-2.5 px-6 text-base',
  lg: 'py-3 px-8 text-lg'
};

// Button component with various options
const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  to,
  href,
  icon,
  iconPosition = 'right',
  className = '',
  disabled = false,
  loading = false,
  onClick,
  type = 'button',
  ariaLabel,
  ...props
}) => {
  // Base classes shared by all button variants
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500';
  
  // Combine variant, size, and custom classes
  const buttonClasses = `${baseClasses} ${variants[variant]} ${sizes[size]} ${className} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`;
  
  // Icon rendering function
  const renderIcon = () => {
    if (loading) {
      return (
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      );
    }
    
    if (icon) {
      return icon;
    }
    
    if (to || href) {
      return <ArrowRight className={`w-4 h-4 group-hover:translate-x-1 transition-transform duration-300 ${iconPosition === 'left' ? 'mr-2' : 'ml-2'}`} />;
    }
    
    return null;
  };

  // Content with proper icon positioning
  const content = (
    <>
      {iconPosition === 'left' && renderIcon()}
      <span className={icon && iconPosition === 'left' ? 'ml-2' : ''}>{children}</span>
      {iconPosition === 'right' && renderIcon()}
    </>
  );

  // Return appropriate element based on props
  if (to) {
    return (
      <Link
        to={to}
        className={`${buttonClasses} group`}
        aria-label={ariaLabel || children}
        {...props}
      >
        {content}
      </Link>
    );
  }

  if (href) {
    return (
      <a
        href={href}
        className={`${buttonClasses} group`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={ariaLabel || children}
        {...props}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type={type}
      className={buttonClasses}
      disabled={disabled || loading}
      onClick={onClick}
      aria-label={ariaLabel || children}
      {...props}
    >
      {content}
    </button>
  );
};

export default Button;